import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getHistoryPageData,
  getUserTimeline, 
  getApartmentHistory,
  getActivityLog
} from '../lib/api';
import { 
  ClockCounterClockwise,
  User,
  House,
  Buildings,
  CaretDown,
  MagnifyingGlass,
  FunnelSimple,
  ArrowRight,
  SignIn,
  SignOut,
  PencilSimple,
  Plus,
  Trash,
  FileText,
  EnvelopeSimple,
  Wrench,
  Info
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

// Type definitions
interface ActivityData {
  id: string;
  action?: string;
  timestamp?: string;
  user_id?: string;
  apartment_id?: string;
  property_id?: string;
  entity_type?: string;
  performed_by?: string;
  details?: {
    description?: string;
    notes?: string;
    [key: string]: string | number | boolean | undefined;
  };
}

interface UserData {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  apartment_id?: string;
  move_in_date?: string;
  move_out_date?: string;
}

interface ApartmentData {
  id: string;
  apartment_number?: string;
}

interface PropertyData {
  id: string;
  name?: string;
}

// Activity type icons and colors
const ACTIVITY_CONFIG = {
  'moved_in': { icon: SignIn, color: 'bg-green-100 text-green-600', label: 'Inflyttad' },
  'moved_out': { icon: SignOut, color: 'bg-red-100 text-red-600', label: 'Utflyttad' },
  'info_updated': { icon: PencilSimple, color: 'bg-blue-100 text-blue-600', label: 'Uppdaterad' },
  'created': { icon: Plus, color: 'bg-green-100 text-green-600', label: 'Skapad' },
  'deleted': { icon: Trash, color: 'bg-red-100 text-red-600', label: 'Borttagen' },
  'issue_created': { icon: Wrench, color: 'bg-amber-100 text-amber-600', label: 'Ärende skapat' },
  'issue_resolved': { icon: Wrench, color: 'bg-green-100 text-green-600', label: 'Ärende löst' },
  'document_uploaded': { icon: FileText, color: 'bg-purple-100 text-purple-600', label: 'Dokument uppladdat' },
  'email_sent': { icon: EnvelopeSimple, color: 'bg-blue-100 text-blue-600', label: 'E-post skickat' },
  'default': { icon: Info, color: 'bg-slate-100 text-slate-600', label: 'Händelse' }
};

export default function HistoryPage() {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityData[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [apartments, setApartments] = useState<ApartmentData[]>([]);
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [viewType, setViewType] = useState('all'); // 'all', 'user', 'apartment', 'property'
  const [selectedId, setSelectedId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [historicalResidents, setHistoricalResidents] = useState<UserData[]>([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [viewType, selectedId]);

  const fetchInitialData = async () => {
    try {
      // Single batch request for all data
      const orgId = user?.organization_id || '';
      const data = await getHistoryPageData(orgId);
      setUsers(data.users || []);
      setApartments(data.apartments || []);
      setProperties(data.properties || []);
      // Also set initial activities
      setActivities(data.activities || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    // Skip if initial load already happened and no filter is applied
    if (viewType === 'all' && activities.length > 0 && !selectedId) {
      return;
    }
    
    setLoading(true);
    try {
      let result;
      
      if (viewType === 'user' && selectedId) {
        result = await getUserTimeline(selectedId);
        setHistoricalResidents([]);
      } else if (viewType === 'apartment' && selectedId) {
        result = await getApartmentHistory(selectedId);
        if (result.data && typeof result.data === 'object' && 'residents' in result.data) {
          const data = result.data as { activities: ActivityData[]; residents: UserData[] };
          setHistoricalResidents(data.residents);
          result = { data: data.activities };
        }
      } else if (viewType === 'property' && selectedId) {
        result = await getActivityLog({ property_id: selectedId, organization_id: user?.organization_id });
        setHistoricalResidents([]);
      } else {
        // Already loaded in fetchInitialData
        setHistoricalResidents([]);
        setLoading(false);
        return;
      }
      
      const activitiesData = Array.isArray(result.data) ? result.data : [];
      setActivities(activitiesData as ActivityData[]);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter activities by search
  const filteredActivities = activities.filter(activity => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      activity.action?.toLowerCase().includes(query) ||
      activity.details?.description?.toLowerCase().includes(query) ||
      activity.entity_type?.toLowerCase().includes(query)
    );
  });

  // Group activities by date
  const groupedActivities = filteredActivities.reduce<Record<string, ActivityData[]>>((groups, activity) => {
    const timestamp = activity.timestamp;
    const date = timestamp ? new Date(timestamp).toLocaleDateString('sv-SE') : 'Okänt datum';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {});

  const getActivityConfig = (action: string | undefined) => {
    const key = action as keyof typeof ACTIVITY_CONFIG;
    return ACTIVITY_CONFIG[key] || ACTIVITY_CONFIG.default;
  };

  const getUserName = (userId: string | undefined) => {
    if (!userId) return 'Okänd användare';
    const u = users.find(u => u.id === userId);
    return u?.name || u?.email || 'Okänd användare';
  };

  const getApartmentLabel = (apartmentId: string | undefined) => {
    if (!apartmentId) return 'Okänd lägenhet';
    const a = apartments.find(a => a.id === apartmentId);
    return a ? `Lgh ${a.apartment_number}` : 'Okänd lägenhet';
  };

  const formatTime = (timestamp: string | undefined) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Idag';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Igår';
    }
    return date.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const canView = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'board_member';

  if (!canView) {
    return (
      <div className="p-6 md:p-8">
        <div className="card-flat text-center py-12">
          <ClockCounterClockwise size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Du har inte behörighet att se historik</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="history-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-purple-100 flex items-center justify-center">
          <ClockCounterClockwise size={20} className="text-purple-600" weight="fill" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Historik</h1>
          <p className="text-sm text-slate-500">Tidslinje över alla händelser</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={viewType} onValueChange={(v) => { setViewType(v); setSelectedId(''); }}>
          <SelectTrigger className="w-[180px]" data-testid="view-type-filter">
            <FunnelSimple size={16} className="mr-2" />
            <SelectValue placeholder="Visa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla händelser</SelectItem>
            <SelectItem value="user">Per användare</SelectItem>
            <SelectItem value="apartment">Per lägenhet</SelectItem>
            <SelectItem value="property">Per fastighet</SelectItem>
          </SelectContent>
        </Select>
        
        {viewType === 'user' && (
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-[220px]">
              <User size={16} className="mr-2" />
              <SelectValue placeholder="Välj användare" />
            </SelectTrigger>
            <SelectContent>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {viewType === 'apartment' && (
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-[220px]">
              <House size={16} className="mr-2" />
              <SelectValue placeholder="Välj lägenhet" />
            </SelectTrigger>
            <SelectContent>
              {apartments.map(a => (
                <SelectItem key={a.id} value={a.id}>Lgh {a.apartment_number}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {viewType === 'property' && (
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-[220px]">
              <Buildings size={16} className="mr-2" />
              <SelectValue placeholder="Välj fastighet" />
            </SelectTrigger>
            <SelectContent>
              {properties.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Sök i historik..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Historical Residents (for apartment view) */}
      {viewType === 'apartment' && selectedId && historicalResidents.length > 0 && (
        <div className="card-flat">
          <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
            <User size={18} />
            Boende (nuvarande och historiska)
          </h3>
          <div className="space-y-2">
            {historicalResidents.map(resident => (
              <div 
                key={resident.id} 
                className={`flex items-center justify-between p-3 rounded-lg ${
                  resident.apartment_id === selectedId ? 'bg-green-50 border border-green-200' : 'bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    resident.apartment_id === selectedId ? 'bg-green-200' : 'bg-slate-200'
                  }`}>
                    <User size={14} className={resident.apartment_id === selectedId ? 'text-green-700' : 'text-slate-500'} />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{resident.name || 'Okänt namn'}</div>
                    <div className="text-xs text-slate-500">{resident.email || resident.phone}</div>
                  </div>
                </div>
                <div className="text-right">
                  {resident.apartment_id === selectedId ? (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Nuvarande</span>
                  ) : (
                    <span className="text-xs text-slate-500">
                      {resident.move_out_date ? `Utflyttad ${new Date(resident.move_out_date).toLocaleDateString('sv-SE')}` : 'Tidigare boende'}
                    </span>
                  )}
                  {resident.move_in_date && (
                    <div className="text-xs text-slate-400 mt-0.5">
                      Inflyttad {new Date(resident.move_in_date).toLocaleDateString('sv-SE')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 w-32 bg-slate-200 rounded mb-3" />
              <div className="space-y-2">
                {[1, 2].map(j => (
                  <div key={j} className="h-16 bg-slate-100 rounded-lg" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : Object.keys(groupedActivities).length === 0 ? (
        <div className="card-flat">
          <div className="empty-state py-12">
            <ClockCounterClockwise size={48} className="empty-state-icon" />
            <p className="text-slate-500">Ingen historik hittades</p>
            {viewType !== 'all' && !selectedId && (
              <p className="text-sm text-slate-400 mt-2">Välj en {viewType === 'user' ? 'användare' : viewType === 'apartment' ? 'lägenhet' : 'fastighet'} för att se historik</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedActivities).map(([date, dayActivities]) => (
            <div key={date}>
              <h3 className="text-sm font-medium text-slate-500 mb-3 sticky top-0 bg-white py-1">
                {formatDate(date)}
              </h3>
              <div className="relative pl-6 border-l-2 border-slate-200 space-y-4">
                {dayActivities.map((activity, index) => {
                  const config = getActivityConfig(activity.action);
                  const Icon = config.icon;
                  
                  return (
                    <div 
                      key={activity.id || index} 
                      className="relative"
                      data-testid={`activity-${activity.id}`}
                    >
                      {/* Timeline dot */}
                      <div className={`absolute -left-[25px] w-4 h-4 rounded-full ${config.color} flex items-center justify-center`}>
                        <Icon size={10} weight="bold" />
                      </div>
                      
                      {/* Activity card */}
                      <div className="card-flat ml-4 hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${config.color}`}>
                                {config.label}
                              </span>
                              <span className="text-xs text-slate-400">
                                {formatTime(activity.timestamp)}
                              </span>
                            </div>
                            
                            <p className="text-sm text-slate-700">
                              {activity.details?.description || (
                                <>
                                  {activity.entity_type === 'resident' && (
                                    <>
                                      <span className="font-medium">{getUserName(activity.user_id)}</span>
                                      {activity.action === 'moved_in' && ' flyttade in'}
                                      {activity.action === 'moved_out' && ' flyttade ut'}
                                      {activity.action === 'info_updated' && ' uppdaterades'}
                                      {activity.apartment_id && (
                                        <> i <span className="font-medium">{getApartmentLabel(activity.apartment_id)}</span></>
                                      )}
                                    </>
                                  )}
                                  {activity.entity_type === 'issue' && (
                                    <>
                                      Ärende {activity.action === 'created' ? 'skapades' : activity.action === 'resolved' ? 'löstes' : 'uppdaterades'}
                                      {activity.details?.title && `: ${activity.details.title}`}
                                    </>
                                  )}
                                  {activity.entity_type === 'document' && (
                                    <>
                                      Dokument {activity.action === 'uploaded' ? 'laddades upp' : activity.action}
                                      {activity.details?.name && `: ${activity.details.name}`}
                                    </>
                                  )}
                                  {!activity.entity_type && activity.action}
                                </>
                              )}
                            </p>
                            
                            {activity.details?.notes && (
                              <p className="text-xs text-slate-500 mt-1">{activity.details.notes}</p>
                            )}
                          </div>
                          
                          {activity.performed_by && (
                            <div className="text-xs text-slate-400 text-right">
                              av {getUserName(activity.performed_by)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
